import { format } from 'date-fns'

import { Typography } from '@/components/typography/typography'
import { useReservation } from '@/features/reservation'
import { cn } from '@/libs/tw'
import { useCartStore } from '@/stores/cart-store/cartStore'
import { CartService } from '@/stores/cart-store/slices/types'
import { formatCurrency } from '@/utils/formatCurrency'
import { translate } from '@/utils/translator'

import { Legal } from './forms/legalForm'

type GuestDataSummaryProps = {
	showLegal?: boolean
}

export const GuestDataSummary = ({ showLegal }: GuestDataSummaryProps) => {
	const location = useCartStore.use.location()
	const { code } = useCartStore.use.promotion()
	const { data: reservation } = useReservation()

	const totalTax = reservation?.total.taxes.filter((tax) => tax.inclusive)
	const touristTax = reservation?.total.taxes.find((tax) => !tax.inclusive)?.description

	if (!reservation) return null

	const getServicePricing = (servicePricing: CartService['servicePricing'], quantity: number) => {
		if (servicePricing === 'Per night') {
			return `${reservation.nights}x ${translate('room-selection-service-perNight')}`
		}
		if (servicePricing === 'Per person') {
			return `${reservation.adultNumber}x ${translate('room-selection-service-perPerson')}`
		}
		if (servicePricing === 'Per person per night') {
			return `${reservation.adultNumber}x ${translate('room-selection-service-perPerson')} ${reservation.nights}x ${translate('room-selection-service-perNight')}`
		}

		return quantity > 1 ? `${quantity}x` : ''
	}

	return (
		<div
			className={cn(
				'mt-24 flex w-full flex-col gap-24 text-userData-textMobile md:w-[752px] md:rounded-8 md:bg-naturel md:p-24 md:text-userData-text'
			)}
		>
			<div
				className={cn(
					'flex flex-col gap-12 border-b border-naturel pb-32 md:border-userData-text'
				)}
			>
				<Typography style={'h3'} element="h3">
					{translate('guest-data-summary-title')}
				</Typography>
				<Typography style={'p-m-bold'} element="h4">
					{location?.title}
				</Typography>
				<Typography style={'p-m-bold'} element="p">
					{`${format(new Date(reservation.arrivalDate), 'dd.MM.yyyy')} - ${format(new Date(reservation.departureDate), 'dd.MM.yyyy')} ( ${reservation.nights > 1 ? translate('travel-period-nights', [reservation.nights]) : translate('travel-period-night', [reservation.nights])} )`}
				</Typography>
			</div>
			<div
				className={cn(
					'flex flex-col gap-12 border-b border-naturel pb-32 md:border-userData-text'
				)}
			>
				<div className={cn('flex items-start justify-between')}>
					<Typography style={'p-m-bold'} element="span">
						{translate('guest-data-summary-total')}{' '}
						{reservation.employeeBookingDetails?.employeeBookingEligibility &&
							`(${code})`}
					</Typography>
					<div className="flex flex-col justify-end text-right">
						{reservation.employeeBookingDetails?.employeeBookingEligibility && (
							<Typography style={'p-m-bold'} element="span" className="line-through">
								{formatCurrency(
									reservation.employeeBookingDetails?.employeeMasterTotal
										.amountAfterTax ?? 0,
									true
								)}
							</Typography>
						)}
						<Typography style={'p-m-bold'} element="span">
							{formatCurrency(reservation.total.amountAfterTax, true)}
						</Typography>
					</div>
				</div>
				{totalTax?.map((tax) => (
					<div className={cn('flex justify-between')}>
						<Typography style={'p'} element="span">
							{translate('guest-data-summary-tax', [tax.percentage ?? ''])}
						</Typography>
						<Typography style={'p'} element="span">
							{formatCurrency(tax.amount ?? 0, true)}
						</Typography>
					</div>
				))}
				{reservation.employeeBookingDetails?.employeeBookingEligibility && (
					<div className={cn('flex justify-between')}>
						<Typography style={'p'} element="span">
							{translate('guest-data-summary-taxable-amount')}
						</Typography>
						<Typography style={'p'} element="span">
							{formatCurrency(
								reservation.employeeBookingDetails.employeeTaxableAmount ?? 0,
								true
							)}
						</Typography>
					</div>
				)}
				{touristTax && (
					<Typography style={'p'} element="span">
						{touristTax}
					</Typography>
				)}
			</div>
			{reservation.roomStays.map((room, index) => {
				return (
					<div
						key={'guest-summary-room' + index}
						className={cn(
							'flex flex-col gap-12 border-b border-naturel pb-32 md:border-userData-text'
						)}
					>
						<div className={cn('flex justify-between')}>
							<Typography style={'p-bold'} element="h4">
								{room.roomType.title}
							</Typography>
							<Typography style={'p-bold'} element="span">
								{formatCurrency(room.total.amountAfterTax, true)}
							</Typography>
						</div>
						<Typography style={'p'} element="span">
							{room.ratePlan.title}
						</Typography>
						{room.services?.map((service) => (
							<Typography
								key={'guest-summary-' + service.serviceCode}
								style={'p'}
								element="span"
							>
								{service.title}{' '}
								<small>
									{service.price
										? getServicePricing(
												service.servicePricing,
												service.quantity
											)
										: `${service.quantity}x`}
								</small>
							</Typography>
						))}
						{room.freeCancellationDeadline ? (
							<Typography style={'p'} element="span">
								{translate('room-selection-cancel-till')}{' '}
								{format(
									new Date(room.freeCancellationDeadline),
									'dd.MM.yyy / HH:mm'
								)}
							</Typography>
						) : (
							<Typography style={'p'} element="span">
								{translate('guest-data-summary-no-cancel')}
							</Typography>
						)}
					</div>
				)
			})}
			<div className={cn('-mt-24')}>
				<Typography style={'p-s'} element="span">
					{translate('guest-data-summary-tex-included')}
				</Typography>
			</div>
			{showLegal && <Legal roomStays={reservation.roomStays} />}
		</div>
	)
}
